import type { HorizontalSide, VerticalSide } from '@app-types/styles'
import type { EntryMeta } from '@app-types/sys'
import { asVideoType } from '@app-types/video'
import { Box } from '@components/box'
import { Container } from '@components/layout/grid'
import { SectionBlock } from '@components/layout/section-block'
import { Markdown } from '@components/markdown'
import { Row } from '@components/row'
import { Text } from '@components/text'
import { Thumbnail } from '@components/thumbnail'
import { ActionButton } from '@contentful-components/action-button'
import type { ActionProps } from '@contentful-components/action-button'
import { SelfFetchingRichText } from '@contentful-components/rich-text'
import type { SectionOfThumbnailsFragment } from '@graphql/fragments/SectionOfThumbnailsFragment.graphql'
import type { ThumbnailFragment } from '@graphql/fragments/ThumbnailFragment.graphql'
import type { ArticleKind } from '@graphql/hooks/article-page'
import { useSectionOfThumbnails } from '@graphql/hooks/section-of-thumbnails'
import { EMPTY_COLLECTION, EMPTY_SECTION } from '@graphql/placeholders'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { webviewStore } from '@lib/store/ui/webview'
import { renderTemplate } from '@lib/utils/text'
import { LINK } from '@shared/util/link'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import type { FunctionComponent } from 'react'

import styles from './section-of-thumbnails.module.scss'

export const SectionOfThumbnails: FunctionComponent<
    SectionOfThumbnailsFragment
> = ({ sys, type, ...restOfProps }) => {
    if (restOfProps?.thumbnails) {
        return (
            <SectionOfThumbnailsResolved
                sys={sys}
                type={type}
                {...restOfProps}
            />
        )
    }
    return <SelfFetchingSectionOfThumbnails sys={sys} type={type} />
}

const SelfFetchingSectionOfThumbnails: FunctionComponent<EntryMeta> = ({
    sys,
}) => {
    const { section } = useSectionOfThumbnails(sys.id)

    return <SectionOfThumbnailsResolved {...section} />
}

/**
 * Renders a section of thumbnails with data from Contentful
 */
const SectionOfThumbnailsResolved: FunctionComponent<
    SectionOfThumbnailsFragment & {
        pad?: (VerticalSide | HorizontalSide)[]
    }
> = ({
    heading,
    headingSlug,
    copy,
    rowLength,
    thumbnails,
    centered,
    fillRow,
    excludeVideos,
    thumbnailAlignment,
    section: { title, action } = EMPTY_SECTION,
    pad = [],
}) => {
    const router = useRouter()

    const filteredThumbnails = useMemo(() => {
        const items = thumbnails?.items ?? []
        if (!excludeVideos) return items
        return items.filter((item) => {
            // Exclude standalone Video type
            if (item.type === 'Video') return false
            // Exclude Article with kind='Video'
            if ('kind' in item && item.kind === 'Video') return false
            return true
        })
    }, [thumbnails, excludeVideos])

    const [first] = filteredThumbnails

    const maxColumns = useMemo(() => {
        if (rowLength) return rowLength
        if (!first) return 3
        if ('type' in first && first.type === 'ArticleTopic') return 4
        if ('size' in first && first.size === 'small') return 4
        return 3
    }, [first, rowLength])

    return (
        <div className={styles.outer}>
            <Container>
                <SectionBlock
                    textAlignOnLargeScreen={centered ? 'center' : undefined}
                >
                    <Box pad={pad}>
                        {(heading || title) && (
                            <div className={styles.heading}>
                                <a
                                    href={LINK.money101Topic(
                                        headingSlug ?? '',
                                        webviewStore.isWebview,
                                    ).serialize(router)}
                                >
                                    <Markdown
                                        tag='div'
                                        typeStyle={{
                                            _: 'heading-xs',
                                            md: 'heading-sm',
                                        }}
                                        markdown={heading ?? title}
                                    />
                                </a>
                            </div>
                        )}
                        {!!copy && (
                            <div
                                className={classNames(
                                    styles['copy-container'],
                                    centered && styles.centered,
                                )}
                            >
                                <Text tag='div' typeStyle='body-lg'>
                                    {copy}
                                </Text>
                            </div>
                        )}

                        <Row
                            fillRow={fillRow}
                            maxColumns={maxColumns}
                            align={
                                (thumbnailAlignment as
                                    | 'left'
                                    | 'center'
                                    | 'right') ?? undefined
                            }
                            className={classNames({
                                [styles['row-with-heading']]: !!(
                                    heading || title
                                ),
                            })}
                            spacing='default'
                        >
                            {filteredThumbnails.map((thumbnail, index) => {
                                return (
                                    <div
                                        className={styles.thumbnail}
                                        key={thumbnail.sys.id}
                                    >
                                        <ThumbnailWrapper
                                            {...thumbnail}
                                            index={index}
                                        />
                                    </div>
                                )
                            })}
                        </Row>

                        <div className={styles.action}>
                            {action && (
                                <ActionButton {...(action as ActionProps)} />
                            )}
                        </div>
                    </Box>
                </SectionBlock>
            </Container>
        </div>
    )
}

const ThumbnailWrapper: FunctionComponent<
    ThumbnailFragment & { index: number }
> = (props) => {
    const router = useRouter()
    const personalizations = usePersonalizations()

    if (props.type === 'Card') {
        const { content: { items: content = [] } = EMPTY_COLLECTION } = props

        return (
            <div className={styles.card}>
                {content.map((text) => (
                    <SelfFetchingRichText
                        type='Text'
                        key={text.sys.id}
                        {...text}
                    />
                ))}
            </div>
        )
    }

    const { title, image } = props

    const variableProps = () => {
        if ('agency' in props) {
            const { agency, href } = props
            return {
                subtitle: agency?.title,
                href,
                target: href,
                fit: 'cover' as const,
            }
        }

        if ('slug' in props && 'category' in props) {
            const { category, slug = '', kind, video } = props

            return {
                subtitle: category?.title,
                href: LINK.post(
                    slug,
                    kind as ArticleKind,
                    webviewStore.isWebview,
                ).serialize(router),
                fit: 'cover' as const,
                video:
                    kind !== 'Video' ? video && asVideoType(video) : undefined, // we dont want to play full videos, we want to show thumbnail
            }
        }

        if ('type' in props && props.type === 'Video') {
            return {
                video: asVideoType(props),
            }
        }

        if ('type' in props && props.type === 'ArticleTopic') {
            const { slug = '' } = props

            return {
                href: LINK.money101Topic(
                    slug,
                    webviewStore.isWebview,
                ).serialize(router),
                fit: 'cover' as const,
                title: '',
            }
        }

        if ('type' in props && props.type === 'ImageLink') {
            const { path } = props

            const isExternalUrl = path?.startsWith('http')

            if (isExternalUrl) {
                return {
                    subtitle: props.subtitle,
                    href: path,
                    openInNewWindow: true,
                    target: '_blank',
                    fit: 'cover' as const,
                }
            }

            const link = LINK.cms({ path })

            return {
                subtitle: props.subtitle,
                href: link.route,
                as: link.serialize(router),
                fit: 'cover' as const,
            }
        }

        if ('action' in props) {
            const { action, size } = props

            if (!action) return { size }

            if ('path' in action) {
                const link = LINK.cms(action)

                return {
                    size,
                    href: link.route,
                    as: link.serialize(router),
                }
            }

            if ('url' in action) return { href: action.url, size }
            return { size }
        }

        if ('size' in props) {
            const size = props.size === 'small' ? 'small' : 'medium'
            return {
                size,
                subtitle: props.subtitle,
            }
        }

        return {}
    }

    return (
        <Thumbnail
            title={renderTemplate(title, personalizations)}
            image={image}
            type={props.type}
            {...variableProps()}
        />
    )
}

export default SectionOfThumbnails
